import { Chip, Switch, Tooltip } from '@heroui/react'
import { IconButton } from '@radix-ui/themes'
import { Pause, Play, RotateCcw, RotateCw } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSessionStorage } from 'usehooks-ts'
import { downloadRecording } from 'utils/funcs/call-logs/downloadRecording'
import WaveSurfer from 'wavesurfer.js'

function AudioPlayer({
  audioBlob,
  keyMarkers = [],
  call,
  onMarkerClick,
  metrics,
}) {
  const [autoplay, setAutoplay] = useSessionStorage('autoplay', false)
  const [isPlaying, setIsPlaying] = useState(autoplay)
  const [isLoading, setIsLoading] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [playbackSpeed, setPlaybackSpeed] = useState(1)
  const waveformRef = useRef(null)
  const waveformId = useRef(`waveform-${Math.random().toString(36).substr(2, 9)}`)

  const startedAt = call?.started_at

  const speedOptions = [
    { label: '1x', value: 1 },
    { label: '1.5x', value: 1.5 },
    { label: '2x', value: 2 },
  ]

  const calculateAirTimePercentages = useCallback(() => {
    if (!metrics)
      return { user: 0, agent: 0 }
    const total = metrics.audio_user_length + metrics.audio_agent_length
    if (!total)
      return { user: 0, agent: 0 }

    return {
      user: ((metrics.audio_user_length / total) * 100).toFixed(1),
      agent: ((metrics.audio_agent_length / total) * 100).toFixed(1),
    }
  }, [metrics])

  const getMarkerColor = useCallback((marker) => {
    if (marker.type === 'pathway_tag' && marker.details?.color) {
      return marker.details.color
    }
    
    // Use color from details if available (for citations)
    if (marker.details?.color) {
      return marker.details.color
    }

    switch (marker.type) {
      case 'variable_extraction':
        return '#8b5cf6'
      case 'pathway_tag':
        return '#fbbf24'
      case 'decision':
        return '#8b5cf6'
      case 'citation':
        return '#f59e0b' // Amber color for citations
      default:
        return '#8b5cf6'
    }
  }, [])

  const calculateTimestampFromDate = useCallback((markerDate) => {
    if (!markerDate)
      return 0
    if (typeof markerDate === 'number')
      return markerDate
    return markerDate
  }, [])

  const initializeWaveform = useCallback(() => {
    if (waveformRef.current) {
      try {
        waveformRef.current.destroy()
      }
      catch (e) {
        console.warn('Wavesurfer cleanup warning:', e)
      }
      waveformRef.current = null
    }

    if (!audioBlob)
      return

    try {
      const wavesurfer = WaveSurfer.create({
        container: `#${waveformId.current}`,
        height: 100,
        cursorWidth: 2,
        cursorColor: '#A1A1AA',
        dragToSeek: true,
        responsive: true,
        interact: true,
        fillParent: true,
        splitChannels: [
          {
            waveColor: '#74DFA2',
            progressColor: '#D1F4E0',
          },
          {
            waveColor: '#66AAF9',
            progressColor: '#CCE3FD',
            overlay: true,
          },
        ],
        barGap: 2,
        barWidth: 3,
        barRadius: 2,
      })

      waveformRef.current = wavesurfer
      wavesurfer.loadBlob(audioBlob)
      wavesurfer.setPlaybackRate(playbackSpeed)

      wavesurfer.on('ready', () => {
        setDuration(wavesurfer.getDuration())
        setIsLoading(false)
        if (autoplay)
          wavesurfer.play()
      })

      wavesurfer.on('audioprocess', () => {
        setCurrentTime(wavesurfer.getCurrentTime())
      })

      wavesurfer.on('seek', (progress) => {
        setCurrentTime(progress * wavesurfer.getDuration())
      })

      wavesurfer.on('play', () => setIsPlaying(true))
      wavesurfer.on('pause', () => setIsPlaying(false))
    }
    catch (error) {
      console.error('Wavesurfer initialization error:', error)
      setIsLoading(false)
    }
  }, [audioBlob, autoplay])

  useEffect(() => {
    setIsLoading(true)
    initializeWaveform()

    return () => {
      if (waveformRef.current) {
        try {
          waveformRef.current.destroy()
        }
        catch (e) {
          console.warn('Wavesurfer cleanup warning:', e)
        }
        waveformRef.current = null
      }
    }
  }, [initializeWaveform])

  const handleSpeedChange = useCallback((speed) => {
    setPlaybackSpeed(speed)
    if (waveformRef.current) {
      waveformRef.current.setPlaybackRate(speed)
    }
  }, [])

  const togglePlayPause = useCallback(() => {
    if (waveformRef.current) {
      if (isPlaying) {
        waveformRef.current.pause()
      }
      else {
        waveformRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }, [isPlaying])

  const handleSeek = useCallback((offsetSeconds) => {
    if (waveformRef.current) {
      const currentTime = waveformRef.current.getCurrentTime()
      const newTime = Math.max(0, Math.min(currentTime + offsetSeconds, duration))
      waveformRef.current.seekTo(newTime / duration)
    }
  }, [duration])

  const formatTime = useCallback((time) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }, [])

  const handleDownload = useCallback(async () => {
    await downloadRecording(call?.c_id)
  }, [call?.c_id])

  // Keyboard event handlers
  useEffect(() => {
    const handleKeyDown = (event) => {
      const activeElement = document.activeElement
      const isEditableElement = activeElement.tagName === 'INPUT'
        || activeElement.tagName === 'TEXTAREA'
        || activeElement.contentEditable === 'true'
        || activeElement.closest('[contenteditable="true"]')
        || activeElement.closest('.ignore-audio-player')

      if (isEditableElement)
        return

      if (event.code === 'Space') {
        event.preventDefault()
        togglePlayPause()
      }
      else if (event.code === 'ArrowRight') {
        event.preventDefault()
        handleSeek(1)
      }
      else if (event.code === 'ArrowLeft') {
        event.preventDefault()
        handleSeek(-1)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [togglePlayPause, handleSeek])

  const handleMarkerClick = useCallback((marker) => {
    if (waveformRef.current && duration > 0) {
      const timestamp = calculateTimestampFromDate(marker.timestamp)
      waveformRef.current.seekTo(timestamp / duration)
      setCurrentTime(timestamp)
    }

    if (onMarkerClick) {
      onMarkerClick(marker)
    }
  }, [duration])

  const renderMarkerIndicators = useCallback(() => {
    if (!keyMarkers?.length || !duration)
      return null

    return (
      <div className="pointer-events-none absolute top-0 right-0 left-0 h-full">
        {keyMarkers.map((marker, index) => {
          const position = (calculateTimestampFromDate(marker.timestamp) / duration) * 100
          const markerColor = getMarkerColor(marker)
          const [markerName, markerDescription] = marker.name.split(':')

          if (position < 0 || position > 100)
            return null

          const tooltipContent = (
            <div>
              <span className="flex items-center justify-between gap-1">
                <div className="font-medium text-wrap">{markerName}</div>
                <div className="text-tiny text-default-500">{formatTime(calculateTimestampFromDate(marker.timestamp))}</div>
              </span>
              <div className="font-geist-mono text-tiny text-wrap">{markerDescription}</div>
              {marker.type === 'citation' && marker.details?.utteranceText && (
                <div className="mt-1 text-3xs text-gray-500 italic">"{marker.details.utteranceText}"</div>
              )}
            </div>
          )

          return (
            <Tooltip radius="none" key={index} content={tooltipContent} classNames={{ base: 'max-w-64 w-fit' }}>
              <div
                className="pointer-events-auto absolute top-0 z-10 h-full"
                style={{ left: `${position}%` }}
              >
                <div
                  className={`
                    ${marker.type === 'citation' 
                      ? 'mt-1 -ml-1.5 h-3 w-3 cursor-pointer rounded-sm ring-2' 
                      : 'mt-1 -ml-2 h-4 w-4 cursor-pointer rounded-full ring-2'}
                    transition-transform duration-200
                    hover:scale-125
                  `}
                  style={{
                    backgroundColor: markerColor,
                    ringColor: `${markerColor}33`,
                  }}
                  onClick={() => handleMarkerClick(marker)}
                />
              </div>
            </Tooltip>
          )
        })}
      </div>
    )
  }, [keyMarkers])

  // External seek event handling
  useEffect(() => {
    const handleSeekEvent = (event) => {
      const { timestamp } = event.detail
      if (waveformRef.current && duration > 0 && startedAt) {
        let seekTime = 0

        if (timestamp instanceof Date || typeof timestamp === 'string') {
          const timestampDate = new Date(timestamp)
          const startDate = new Date(startedAt)
          seekTime = (timestampDate - startDate) / 1000
        }
        else if (typeof timestamp === 'number') {
          seekTime = timestamp
        }

        const boundedTime = Math.min(Math.max(0, seekTime), duration)
        waveformRef.current.seekTo(boundedTime / duration)
        setCurrentTime(boundedTime)
      }
    }

    const container = document.querySelector('.audio-player-container')
    if (container) {
      container.addEventListener('seek-to-timestamp', handleSeekEvent)
    }

    return () => {
      if (container) {
        container.removeEventListener('seek-to-timestamp', handleSeekEvent)
      }
    }
  }, [duration, startedAt])

  return (
    <div className={`
      audio-player-container flex h-full w-full max-w-full flex-col
      justify-between divide-y divide-gray-200/30 bg-white/80 p-4
      backdrop-blur-xs
    `}
    >
      <div className="my-auto flex h-fit items-end gap-4" tabIndex="0">
        <div className="flex h-full flex-col justify-between">
          <div className="flex items-center justify-center gap-2">
            <IconButton
              onClick={() => handleSeek(-10)}
              disabled={isLoading}
              className={`
                transition-colors
                hover:bg-violet-50
              `}
              style={{
                background: 'rgba(109, 100, 239, 0.1)',
                border: '1px solid rgba(109, 100, 239, 0.2)',
                borderRadius: '50%',
                cursor: 'pointer',
              }}
            >
              <RotateCcw size={12} className="text-violet-600" />
            </IconButton>

            <IconButton
              onClick={togglePlayPause}
              disabled={isLoading}
              className={`
                transition-colors
                hover:bg-violet-50
              `}
              style={{
                background: 'rgba(109, 100, 239, 0.1)',
                border: '1px solid rgba(109, 100, 239, 0.2)',
                borderRadius: '50%',
                padding: '2px',
                cursor: 'pointer',
              }}
            >
              {isLoading
                ? <RotateCw size={15} className="animate-spin text-violet-600" />
                : isPlaying
                  ? <Pause size={15} className="text-violet-600" />
                  : <Play size={15} className="text-violet-600" />}
            </IconButton>

            <IconButton
              onClick={() => handleSeek(10)}
              disabled={isLoading}
              className={`
                transition-colors
                hover:bg-violet-50
              `}
              style={{
                background: 'rgba(109, 100, 239, 0.1)',
                border: '1px solid rgba(109, 100, 239, 0.2)',
                borderRadius: '50%',
                cursor: 'pointer',
              }}
            >
              <RotateCw size={12} className="text-violet-600" />
            </IconButton>
          </div>

          <div className="flex w-full items-center gap-2">
            <div className="mx-auto flex">
              {speedOptions.map(option => (
                <button
                  type="button"
                  key={option.value}
                  onClick={() => handleSpeedChange(option.value)}
                  className={`
                    rounded-full px-2 py-1 text-2xs transition-colors
                    ${
                playbackSpeed === option.value
                  ? 'bg-violet-100 text-violet-600'
                  : `
                    bg-gray-50 text-gray-500
                    hover:bg-violet-50
                  `
                }
                  `}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <span className="flex w-full justify-between text-tiny">
            <Chip variant="flat" color="success" size="sm" radius="none">
              Human
            </Chip>
            <Chip variant="flat" color="primary" size="sm" radius="none">
              Assistant
            </Chip>
          </span>
        </div>

        <div className="relative grow">
          <div id={waveformId.current} className="overflow-hidden rounded-lg" />
          {renderMarkerIndicators()}
        </div>
      </div>

      <div className="flex items-center gap-4 pt-2 text-xs text-gray-400">
        {(metrics?.audio_user_length || metrics?.audio_agent_length) && (
          <>
            <span>
              Agent:
              {' '}
              {calculateAirTimePercentages().agent}
              %
            </span>
            <span>
              User:
              {' '}
              {calculateAirTimePercentages().user}
              %
            </span>
            <span>
              Overlap:
              {' '}
              {metrics?.audio_overlap_seconds?.toFixed(2)}
              s
            </span>
          </>
        )}

        <button
          type="button"
          onClick={handleDownload}
          className={`
            text-violet-500 underline transition-colors
            hover:text-violet-600
          `}
        >
          Download
        </button>
        <div className="font-mono text-violet-600/70">
          {formatTime(currentTime)}
          {' '}
          /
          {formatTime(duration)}
        </div>
        <Switch
          size="sm"
          thumbIcon={<Play size={10} stroke="blue" fill="blue" />}
          classNames={{ label: 'text-xs' }}
          className="ml-auto"
          isSelected={autoplay}
          onValueChange={setAutoplay}
        >
          Autoplay Recordings
        </Switch>
      </div>
    </div>
  )
}

export default AudioPlayer
